import type { Theme } from '@mui/material/styles';

import TimelineItem from '@mui/lab/TimelineItem';
import { styled, Typography } from '@mui/material';
import TimelineContent from '@mui/lab/TimelineContent';

export const StyledTooltipLabel = styled('span')<{ color?: string }>(({ theme, color }) => ({
  color: color || theme?.vars?.palette.text.secondary,
  textDecoration: 'underline', // Adds underline
  textDecorationStyle: 'dotted', // Makes the underline dotted
  textUnderlineOffset: '2px', // Adjusts the distance of the underline from the text
  '&:hover': {
    textDecorationStyle: 'dotted', // Changes underline to solid on hover
    textDecorationColor: theme?.vars?.palette.primary.main, // Changes underline color to blue on hover
  },
}));

// Styled TimelineItem
export const StyledTimelineItem = styled(TimelineItem)(({ theme, color }) => ({
  paddingLeft: '1.5rem',
  paddingRight: '1.5rem',
  backgroundColor:
    color === 'error'
      ? 'rgba(255, 86, 48, 0.12)' // 12% transparency for error events
      : color === 'grey'
        ? 'rgba(145, 158, 171, 0.12)' // 12% transparency for grey events
        : 'rgba(0, 0, 0, 0)',
  '&:before': {
    display: 'none', // Removes the default left padding of TimelineItem
  },
  // Ensure WCAG AA contrast on colored backgrounds (error/grey tinted rows)
  ...(color === 'error' || color === 'grey'
    ? {
        '& .MuiTypography-root': {
          color: theme.vars.palette.text.primary,
        },
      }
    : {}),
}));

// Styled TimelineContent
export const StyledTimelineContent = styled(TimelineContent)(({ theme }) => ({
  textAlign: 'left', // Ensures the content is left-aligned
  marginLeft: theme.spacing(2), // Adds spacing between the dot and the content
  padding: theme.spacing(1), // Optional padding for better spacing
  marginTop: 0.5,
}));

// Styled Typography for the title
export const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme?.vars?.palette.text.primary,
  textDecoration: 'underline',
}));

// Styled Typography for the description and date
export const StyledDescription = styled(Typography)(({ theme }) => ({
  color: theme?.vars?.palette.text.secondary,
}));

export const scrollbarStyles = (theme: Theme) => ({
  '&::-webkit-scrollbar': {
    width: '0.5em',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: '0.25em',
    transition: 'background-color 0.3s',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.grey[600],
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  '&:hover': {
    scrollbarColor: `${theme.palette.grey[400]} transparent`,
  },
});
